/**
 * Entity visibility filter — pure, side-effect-free predicate that decides
 * which entities appear in the sidebar / tree.
 *
 * Used by the spaceEntityStore-backed sidebar selectors and any other
 * caller that needs the canonical "is this entity user-visible?" check.
 * `buildDisplayableEntities` additionally injects the synthetic "Chats"
 * group used by the sidebar.
 */

import type { BaseEntityType } from '../types/entities';

/**
 * Entity types that never appear in the file tree, no matter what.
 */
export const HIDDEN_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'DocVersion',
  'Message',
  'CommentThread',
  'Thread',
  'Space',
  'Window',
  'SearchResults',
  'ManuscriptDiscoveryJob',
  'APIConnection',
  'Integration',
  'BankTransaction',
  'PlaidItem',
  'PlaidTransaction',
  'PlaidAccount',
  'GoogleDriveFile',
  'Mail.Contact',
  'RiverAppStorage',
  'Task',
  'Area',
  'Job',
  'Social.Tweet',
  'Schedule',
  'Terminal',
  'Mail.Message',
  'Mail.Thread',
  'Mail.Folder',
  'Mail.Draft',
  'Messaging.Message',
  'Crisp.Message',
  'TableRow',
  'Icd10xQuery',
  // Legacy intelligence/memory types — apps deleted, rows orphaned in prod.
  // Hidden so they don't appear as unknown-type entries in generic entity lists.
  'Memory',
  'System.Memory',
  'System.UserProfile',
  // Admin-only — managed via the admin invite system; must never surface in the
  // file tree or sidebar for any user (including admins, who use /api/invites).
  'InviteLink',
]);

/**
 * Return true when an entity should be displayed in the sidebar / tree.
 *
 * Trashed entities and integration sub-types are excluded; embedded children,
 * hidden flags, pasted-image filenames and local-file integrations are also
 * dropped.
 */
export function isVisibleEntity(entity: BaseEntityType): boolean {
  if ((entity as any).trashed === true) return false;

  if (HIDDEN_ENTITY_TYPES.has(entity.entityType) || (entity.skeleton as any)?.isDuplicate) {
    return false;
  }

  if (entity.entityType?.startsWith('Integration.')) return false;

  if (entity.entityType === 'LocalFile') {
    // Auto-synced integration LocalFiles (no sourceImportMethod) are hidden to
    // avoid cluttering the sidebar. Explicitly-imported files (e.g. "Keep as
    // Word File") carry sourceImportMethod and should remain visible.
    const importMethod = (entity.skeleton as any)?.sourceImportMethod;
    if (!importMethod) return false;
  }

  if (entity.entityType?.startsWith('External.LocalFile')) {
    return false;
  }

  const skeleton = (entity.skeleton as any) || {};
  if (skeleton.isEmbedded === true || skeleton.embedded === true || skeleton.parentEntityId) {
    return false;
  }
  if ((entity as any).hidden === true || skeleton.hidden === true) {
    return false;
  }

  if (entity.entityType?.startsWith('File.Image')) {
    if (entity.name?.match(/^pasted-image-\d+\.(png|jpg|jpeg|gif|webp)$/i)) {
      return false;
    }
  }

  return true;
}

/**
 * Filter an array of entities to only those that should appear in the tree.
 */
export function filterVisibleEntities<T extends BaseEntityType>(entities: readonly T[]): T[] {
  return entities.filter(isVisibleEntity);
}

/** Synthetic system entity IDs (must match `stores/systemEntityIds.ts`). */
export const SYSTEM_ENTITY_IDS = {
  SPACECHAT: '__spacechat__',
  INTEGRATIONS: '__integrations__',
} as const;

const STABLE_SYSTEM_TIMESTAMP = new Date('2026-01-01T00:00:00Z').toISOString();

/**
 * Filter to visible entities and append the synthetic "Chats" group used by
 * the sidebar. Returns a new array; does not mutate input.
 */
export function buildDisplayableEntities<T extends BaseEntityType>(
  entities: readonly T[],
): T[] {
  const filtered = entities.filter(isVisibleEntity);

  const threads = filtered.filter((e) => e.entityType === 'Thread');
  const orphanedThreads = threads.filter((t) => {
    const groupIds = (t.skeleton as any)?.groupIds || [];
    return groupIds.length === 0;
  });
  const orphanedIds = new Set(orphanedThreads.map((t) => t._id));

  const result = filtered.filter((e) => !orphanedIds.has(e._id));

  if (orphanedThreads.length > 0) {
    const spaceChat = {
      _id: SYSTEM_ENTITY_IDS.SPACECHAT,
      id: SYSTEM_ENTITY_IDS.SPACECHAT,
      entityType: 'Group',
      type: 'group',
      name: 'Chats',
      skeleton: {
        '@type': 'Group',
        groupName: 'Chats',
        childEntities: orphanedThreads.map((t) => t._id),
        isSystemGroup: true,
      } as any,
      children: orphanedThreads,
      created_at: STABLE_SYSTEM_TIMESTAMP,
      updated_at: STABLE_SYSTEM_TIMESTAMP,
    } as unknown as T;
    result.unshift(spaceChat);
  }

  return result;
}
